import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/providers/TenantProvider'
import {
  facilityApi,
  type BuildingInput,
  type FacilityInput,
  type FloorInput,
  type SitePlanApply,
  type SitePlanProposal,
} from '@/api/facilityApi'
import { equipmentApi } from '@/api/equipmentApi'
import type { SitePlanReading } from '@/lib/sitePlanPicture'

/** The site layout (z8nrz7f731). Keyed by tenant so a switch never shows another site. */
export function useSiteLayout(enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['facility-layout', tenantId],
    enabled: !!tenantId && enabled,
    queryFn: () => facilityApi.layout(tenantId as string),
  })
}

/** Query key root for where equipment stands; refreshed by every layout write and every room change. */
export const FACILITY_LOCATIONS_KEY = 'facility-locations'

/**
 * Where every piece of equipment stands (z8nrz7fw25), for the room picker and
 * the Storage "Locations" view. Off when `enabled` is false — callers gate it
 * on the permissions the route accepts, so it never 403s quietly.
 */
export function useFacilityLocations(enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [FACILITY_LOCATIONS_KEY, tenantId],
    enabled: !!tenantId && enabled,
    queryFn: () => facilityApi.locations(tenantId as string),
  })
}

/**
 * Put a piece of equipment in a room, or take it out with null (z8nrz7fw25).
 * The server refuses a unit an approved transfer is moving stock through.
 */
export function useSetEquipmentRoom() {
  const { selectedTenantId } = useTenant()
  const qc = useQueryClient()
  return useMutation<unknown, Error, { equipmentId: string; roomId: string | null }>({
    mutationFn: ({ equipmentId, roomId }) =>
      equipmentApi.updateEquipment(selectedTenantId as string, equipmentId, { roomId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [FACILITY_LOCATIONS_KEY] })
      void qc.invalidateQueries({ queryKey: ['equipment'] })
    },
  })
}

/** The room types a new room can be made as: equipment types of purpose ROOM. */
export function useRoomTypes(enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['equipment', 'room-types', tenantId],
    enabled: !!tenantId && enabled,
    queryFn: () => equipmentApi.listTypes(tenantId as string, ['ROOM']),
  })
}

/**
 * Make a room and put it on a floor in one step (z8nrz7fw25) — what "+ Room"
 * on the studio's floor does. A room is equipment of a ROOM type, so it is
 * created the way the Rooms page creates one, then placed.
 */
export function useCreateRoom() {
  const { selectedTenantId } = useTenant()
  const tenantId = selectedTenantId as string
  const qc = useQueryClient()
  return useMutation<{ id: string }, Error, { label: string; name?: string; typeId: string; floorId: string }>({
    mutationFn: async ({ label, name, typeId, floorId }) => {
      const room = await equipmentApi.createEquipment(tenantId, {
        label,
        equipmentTypeId: typeId,
        ...(name ? { description: name } : {}),
      })
      await facilityApi.setRoomFloor(tenantId, room.id, floorId)
      return { id: room.id }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: [FACILITY_LOCATIONS_KEY] })
      void qc.invalidateQueries({ queryKey: ['facility-layout'] })
      void qc.invalidateQueries({ queryKey: [FACILITY_OVERVIEW_KEY] })
      void qc.invalidateQueries({ queryKey: ['equipment'] })
    },
  })
}

/** Rename a room: its ID (label) and what it is ("Cryo Storage", its description). */
export function useRenameRoom() {
  const { selectedTenantId } = useTenant()
  const qc = useQueryClient()
  return useMutation<unknown, Error, { id: string; label?: string; name?: string }>({
    mutationFn: ({ id, label, name }) =>
      equipmentApi.updateEquipment(selectedTenantId as string, id, {
        ...(label !== undefined ? { label } : {}),
        ...(name !== undefined ? { description: name } : {}),
      }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: [FACILITY_LOCATIONS_KEY] })
      void qc.invalidateQueries({ queryKey: ['facility-layout'] })
      void qc.invalidateQueries({ queryKey: ['equipment'] })
    },
  })
}

/**
 * A plain "Room" type, for a tenant that has none yet, so the first room can
 * be made without leaving the studio. Put in the first room group, or a new
 * "Rooms" group. Not on a cleaning cycle: that is set on the Rooms page.
 */
export function useCreateRoomType() {
  const { selectedTenantId } = useTenant()
  const tenantId = selectedTenantId as string
  const qc = useQueryClient()
  return useMutation<{ id: string }, Error, void>({
    mutationFn: async () => {
      const groups = await equipmentApi.listGroups(tenantId, ['ROOM'])
      const group = groups.find((g) => g.forRooms) ?? (await equipmentApi.createGroup(tenantId, { name: 'Rooms', forRooms: true }))
      const type = await equipmentApi.createType(tenantId, { name: 'Room', groupId: group.id, purpose: 'ROOM', maintained: false })
      return { id: type.id }
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['equipment'] }),
  })
}

/** Query key root, so whatever changes a room's status (a task, a hold time) can refresh the Overall View. */
export const FACILITY_OVERVIEW_KEY = 'facility-overview'

/**
 * Every room's facts for the Overall View. Fresh on every visit and window
 * focus (forms are filled on another page), refreshed each minute, and
 * invalidated by the writes that change a room's status.
 */
export function useFacilityOverview(enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [FACILITY_OVERVIEW_KEY, tenantId],
    enabled: !!tenantId && enabled,
    queryFn: () => facilityApi.overview(tenantId as string),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  })
}

/**
 * Every layout write. Each one refreshes the layout and the room list too: the
 * red "not mapped" marker on the Room list follows a room's floor.
 */
export function useFacilityMutations() {
  const { selectedTenantId } = useTenant()
  const tenantId = selectedTenantId as string
  const qc = useQueryClient()
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['facility-layout'] })
    void qc.invalidateQueries({ queryKey: [FACILITY_OVERVIEW_KEY] })
    void qc.invalidateQueries({ queryKey: [FACILITY_LOCATIONS_KEY] })
    void qc.invalidateQueries({ queryKey: ['equipment'] })
  }
  const m = <A, R>(fn: (args: A) => Promise<R>) => useMutation<R, Error, A>({ mutationFn: fn, onSuccess: refresh })

  return {
    createFacility: m((input: FacilityInput) => facilityApi.createFacility(tenantId, input)),
    updateFacility: m(({ id, ...input }: Partial<FacilityInput> & { id: string; sortOrder?: number }) =>
      facilityApi.updateFacility(tenantId, id, input),
    ),
    deleteFacility: m((id: string) => facilityApi.deleteFacility(tenantId, id)),
    createBuilding: m((input: BuildingInput) => facilityApi.createBuilding(tenantId, input)),
    updateBuilding: m(({ id, ...input }: Partial<BuildingInput> & { id: string; sortOrder?: number }) =>
      facilityApi.updateBuilding(tenantId, id, input),
    ),
    deleteBuilding: m((id: string) => facilityApi.deleteBuilding(tenantId, id)),
    uploadSchematic: m(({ id, file }: { id: string; file: File }) => facilityApi.uploadSchematic(tenantId, id, file)),
    removeSchematic: m((id: string) => facilityApi.removeSchematic(tenantId, id)),
    createLink: m((input: Parameters<typeof facilityApi.createLink>[1]) => facilityApi.createLink(tenantId, input)),
    updateLink: m(({ id, ...input }: { id: string } & Parameters<typeof facilityApi.updateLink>[2]) => facilityApi.updateLink(tenantId, id, input)),
    deleteLink: m((id: string) => facilityApi.deleteLink(tenantId, id)),
    matchSitePlan: useMutation<SitePlanProposal, Error, SitePlanReading>({ mutationFn: (reading) => facilityApi.matchSitePlan(tenantId, reading) }),
    applySitePlan: m((input: SitePlanApply) => facilityApi.applySitePlan(tenantId, input)),
    createFloor: m((input: FloorInput) => facilityApi.createFloor(tenantId, input)),
    updateFloor: m(({ id, ...input }: Partial<FloorInput> & { id: string }) => facilityApi.updateFloor(tenantId, id, input)),
    deleteFloor: m((id: string) => facilityApi.deleteFloor(tenantId, id)),
    setRoomSize: m(({ roomId, w, d, outline }: { roomId: string; w: number | null; d: number | null; outline?: string | null }) =>
      facilityApi.setRoomSize(tenantId, roomId, w, d, outline),
    ),
    setUnitSpot: m(({ equipmentId, ...input }: { equipmentId: string; x?: number | null; y?: number | null; shape?: string | null }) =>
      facilityApi.setUnitSpot(tenantId, equipmentId, input),
    ),
    setRoomPosition: m(({ roomId, x, y }: { roomId: string; x: number | null; y: number | null }) =>
      facilityApi.setRoomPosition(tenantId, roomId, x, y),
    ),
    setRoomFloor: m(({ roomId, floorId }: { roomId: string; floorId: string | null }) =>
      facilityApi.setRoomFloor(tenantId, roomId, floorId),
    ),
  }
}
